import {
  ClientError,
  ServerError,
  isNonActionableRequestError,
  type RequestError,
} from "./request.ts";

/**
 * True when a GET on a parameterised path (e.g. `/admin/parsings/:id`)
 * returns 404. These represent user-navigable-resource lookups where the
 * resource was deleted / the ID was stale / the URL was typed — the
 * frontend is expected to render a "not found" UI, not alert on-call.
 *
 * Index/list endpoints (no `:` in the path) that 404 ARE real bugs and
 * continue to be reported.
 */
export function isExpectedResourceLookup404(error: RequestError): boolean {
  if (!(error instanceof ClientError)) return false;
  if (error.responseStatus !== 404) return false;
  if (error.method !== "get") return false;
  return error.path.includes(":");
}

/**
 * True when the admin "who am I" bootstrap probe returns 403 — the user
 * authenticated via Firebase but their account lacks an admin role, so the
 * `role(adminRoleSet)` middleware rejects the call. The admin/dashboard
 * frontends handle this in LoginPage by surfacing "Your account does not
 * have admin access" and logging the user out; it's a normal access-control
 * outcome, not a bug. Other admin 403s remain real bugs (broken frontend
 * route gating).
 */
export function isExpectedAdminMe403(error: RequestError): boolean {
  if (!(error instanceof ClientError)) return false;
  if (error.responseStatus !== 403) return false;
  if (error.method !== "get") return false;
  return error.path === "/admin/users/me";
}

type CaptureContext = {
  level?: "error" | "warning";
  tags?: Record<string, string>;
  fingerprint?: string[];
};

type CaptureException = (error: unknown, context?: CaptureContext) => string;

type ApiSentryReporterOptions = {
  /**
   * Extra skip predicate. When it returns true for a given error, the
   * reporter does not call captureException. Composes with the built-in
   * `isNonActionableApiError` skip (either returning true skips capture).
   * Used to suppress expected lifecycle events (e.g. auth-handled
   * session-expiry 401s).
   */
  shouldSkip?: (error: RequestError) => boolean;
};

/**
 * Builds an `onRequestError` handler that forwards api errors to Sentry at
 * level "error" — every 4xx/5xx/network failure that reaches the user is
 * treated as a bug worth investigating.
 *
 * Always skips `isNonActionableRequestError` (page-teardown parse races,
 * offline / CORS-stripped network failures). Caller can pass `shouldSkip`
 * for additional app-specific suppression (e.g. auth-handled 401s).
 *
 * Pass `Sentry.captureException` as the first argument — kept as a parameter
 * so this file stays free of the @sentry/* import (api server doesn't ship
 * Sentry).
 */
export function createApiSentryReporter(
  captureException: CaptureException,
  options: ApiSentryReporterOptions = {},
): (error: RequestError) => void {
  const { shouldSkip } = options;
  return (error) => {
    if (isNonActionableRequestError(error)) return;
    if (shouldSkip?.(error) === true) return;
    const isStatusError =
      error instanceof ClientError || error instanceof ServerError;
    captureException(error, {
      level: "error",
      tags: {
        source: "apiClient",
        api_method: error.method,
        api_path: error.path,
        ...(isStatusError ? { api_status: String(error.responseStatus) } : {}),
      },
      fingerprint: isStatusError
        ? ["api", error.method, error.path, String(error.responseStatus)]
        : ["api", "network", error.method, error.path],
    });
  };
}

/**
 * Compose multiple `onRequestError` handlers into one. Each runs even if a
 * prior one throws, so a Sentry hiccup doesn't break the auth-redirect flow.
 */
export function composeOnRequestError(
  ...handlers: Array<(error: RequestError) => void>
): (error: RequestError) => void {
  return (error) => {
    for (const handler of handlers) {
      try {
        handler(error);
      } catch {
        // Swallow — the next handler still needs to run.
      }
    }
  };
}
